import React, { useMemo } from 'react';
import { Autocomplete, SxProps, TextField, Theme } from '@mui/material';
import { useTheme } from '@mui/material/styles';

export type AppSelectOption = {
  value: string | number;
  label: string;
  disabled?: boolean;
};

export type AppSelectChangeEvent = {
  target: {
    name: string;
    value: string;
  };
};

type AppSelectOptionInput = string | number | AppSelectOption;

interface AppSelectProps {
  label?: string;
  name?: string;
  value?: string | number | null;
  options: AppSelectOptionInput[];
  onChange?(event: AppSelectChangeEvent): void;
  onValueChange?(value: string): void;
  onOpen?(): void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  clearable?: boolean;
  sx?: SxProps<Theme>;
}

const normalizeOptions = (options: AppSelectOptionInput[]): AppSelectOption[] =>
  options.map((option) => (
    typeof option === 'object'
      ? option
      : { value: option, label: String(option) }
  ));

export default function AppSelect({
  label,
  name = '',
  value = '',
  options,
  onChange,
  onValueChange,
  onOpen,
  placeholder = 'Search…',
  required = false,
  disabled = false,
  error = false,
  helperText,
  fullWidth = false,
  size = 'small',
  clearable = false,
  sx,
}: AppSelectProps) {
  const theme = useTheme();
  const normalized = useMemo(() => normalizeOptions(options), [options]);
  const stringValue = value == null ? '' : String(value);

  const selected = useMemo(() => {
    const match = normalized.find((option) => String(option.value) === stringValue);
    if (match) {
      return match;
    }
    if (stringValue === '') {
      return null;
    }
    return { value: stringValue, label: stringValue };
  }, [normalized, stringValue]);

  const emitChange = (nextValue: string) => {
    onChange?.({ target: { name, value: nextValue } });
    onValueChange?.(nextValue);
  };

  return (
    <Autocomplete
      options={normalized}
      value={selected}
      disabled={disabled}
      disableClearable={!clearable && selected !== null}
      fullWidth={fullWidth}
      size={size}
      sx={sx}
      isOptionEqualToValue={(option, current) => String(option.value) === String(current.value)}
      getOptionLabel={(option) => option.label}
      getOptionDisabled={(option) => Boolean(option.disabled)}
      filterOptions={(available, state) => {
        const query = state.inputValue.trim().toLowerCase();
        if (!query) {
          return available;
        }
        return available.filter((option) => (
          option.label.toLowerCase().includes(query)
          || String(option.value).toLowerCase().includes(query)
        ));
      }}
      onOpen={onOpen}
      onChange={(_, next) => emitChange(next ? String(next.value) : '')}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          name={name}
          required={required}
          error={error}
          helperText={helperText}
          placeholder={placeholder}
          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
        />
      )}
      slotProps={{
        paper: {
          sx: {
            borderRadius: `${theme.shape.borderRadius}px`,
            mt: 0.5,
            boxShadow: theme.shadows[4],
          },
        },
        listbox: {
          sx: { maxHeight: 280, py: 0.5 },
        },
      }}
    />
  );
}
